import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Category } from '../../../models/Category'
import useCategorySuggestions from '../../../state/queries/useCategorySuggestions'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import formikConnectedMultiItemSelect from '../../basics/ItemSelect/formikConnectedMultiItemSelect'
import multiItemSelect from '../../basics/ItemSelect/multiItemSelect'
import EditCategoryForm from '../../EditCategoryForm/EditCategoryForm'

import styles from './SelectCategories.module.scss'

/**********************************************************************
 * SelectCategories
 **********************************************************************/
const SelectCategoriesCombobox = multiItemSelect({
  multiSelect: true,
  useSuggestions: useCategorySuggestions,
  renderItemDescription: (category) => category.name,
})

const SelectCategories = React.memo(function SelectCategories({
  allowCreate,
  ...props
}: { allowCreate?: boolean } & React.ComponentProps<typeof SelectCategoriesCombobox>) {
  const { t } = useTranslation()
  const showDialog = useDialog()

  const { setItems } = props
  const handleCreateCategory = useCallback(async () => {
    const category = await showDialog(
      (close) => <EditCategoryForm onClose={close} />,
      () => undefined as Category | undefined
    )
    if (category) {
      setItems((prev) => [...prev, category])
    }
  }, [setItems])

  return (
    <div className={styles.SelectCategories}>
      <SelectCategoriesCombobox {...props} />
      {allowCreate && (
        <Button small onClick={handleCreateCategory}>
          {t('category.cta create')}
        </Button>
      )}
    </div>
  )
})

export default SelectCategories

/**********************************************************************
 * SelectCategoriesFormik
 **********************************************************************/
const _SelectCategoriesFormik = formikConnectedMultiItemSelect({ component: SelectCategories })

export const SelectCategoriesFormik = _SelectCategoriesFormik as React.FunctionComponent<
  React.ComponentProps<typeof _SelectCategoriesFormik> & { allowCreate?: boolean }
>
